/**
 * Доступ к Telegram-аккаунту — окно.
 *
 * Вынесено отдельным файлом: окном пользуются и список аккаунтов, и разбор
 * команд, а копия в двух местах разошлась бы на первой же правке.
 *
 * ── Почему выдача выглядит так ──────────────────────────────────────────────
 *
 * Раньше это была таблица с колонками «Сотрудник / Роль / Уровень» и ссылкой
 * «Отозвать», а под ней — список всех сотрудников подряд, у каждого по две
 * кнопки «Чтение» и «Работа». Понять по такому экрану, у кого доступ уже есть,
 * можно было только сверив два списка глазами, а выдача сводилась к поиску
 * фамилии среди сорока строк.
 *
 * Теперь один список людей с доступом и отдельный поиск для добавления.
 * Уровень переключается прямо в строке, а не выдаётся заново.
 */

import { useCallback, useEffect, useState } from "react";
import type { AnyTelegramAccountDto, UserDto, UserSummaryDto } from "@tw/shared-types";
import { isMaskedAccount } from "@tw/shared-types";
import type { AssignmentDto } from "../lib/api.js";
import { useSession } from "../state/session.tsx";
import { accountLabel } from "./accountLabel.js";
import { describeError } from "../lib/errors.js";
import { tierOf } from "../lib/roles.js";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckIcon, SearchIcon, TrashIcon } from "./icons.tsx";
import { FIELD } from "./controls.js";
import { cn } from "@/lib/utils";

type AnyUser = UserDto | UserSummaryDto;

/** Человеческие названия уровней. В базе они английские и в интерфейсе не место. */
const LEVEL_LABELS: Record<string, string> = {
  READ_ONLY: "Чтение",
  MANAGE: "Работа",
};

export function AccessDialog({
  account,
  users,
  canAssign,
  canAssignSenior,
  onClose,
  onChanged,
}: {
  account: AnyTelegramAccountDto | null;
  users: AnyUser[];
  canAssign: boolean;
  canAssignSenior: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { api } = useSession();

  const [assignments, setAssignments] = useState<AssignmentDto[]>([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const accountId = account?.id ?? null;

  const load = useCallback(async () => {
    if (!accountId) return;
    try {
      const { items } = await api.listAssignments(accountId);
      setAssignments(items);
      setError(null);
    } catch (cause) {
      setError(describeError(cause));
    }
  }, [api, accountId]);

  useEffect(() => {
    if (!accountId) {
      setAssignments([]);
      setQuery("");
      setError(null);
      return;
    }
    void load();
  }, [accountId, load]);

  const act = async (action: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
      onChanged();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const assignedIds = new Set(assignments.map((a) => a.user.id));

  /*
   * Кандидаты — все, кому доступ ещё не выдан.
   *
   * Роли не перечисляем поимённо: добавив новую ветку, её людей забыли бы
   * здесь показать, и причину пришлось бы искать в коде.
   */
  const candidates = users.filter(
    (u) =>
      !assignedIds.has(u.id) &&
      u.status === "ACTIVE" &&
      tierOf(u.role) !== "ADMIN" &&
      (!query.trim() ||
        u.displayName.toLowerCase().includes(query.trim().toLowerCase())),
  );

  const owner =
    account && !isMaskedAccount(account) ? account.seniorManager : null;
  const seniors = users.filter((u) => tierOf(u.role) === "SENIOR");

  return (
    <Dialog open={account !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{account ? accountLabel(account) : ""}</DialogTitle>
          <DialogDescription>
            Кто может работать с этим аккаунтом. Отзыв действует сразу.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-5">
          {error && (
            <p className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Доступ есть у {assignments.length}
            </p>

            {assignments.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Пока ни у кого. Добавьте сотрудника ниже.
              </p>
            ) : (
              <ul className="flex list-none flex-col gap-1.5 p-0">
                {assignments.map((a) => (
                  <li
                    key={a.id}
                    className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-heading">
                        {a.user.displayName}
                      </span>
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {a.user.role}
                        {/*
                          Расхождение записанного и действующего уровня видно
                          сразу: оно означает, что доступ выдан выше потолка
                          роли и часть его не работает.
                        */}
                        {a.effectiveAccessLevel !== a.accessLevel &&
                          ` · выдано ${LEVEL_LABELS[a.accessLevel] ?? a.accessLevel}, действует ${LEVEL_LABELS[a.effectiveAccessLevel] ?? a.effectiveAccessLevel} — выше роль не пускает`}
                      </span>
                    </span>

                    {canAssign && (
                      <LevelSwitch
                        value={a.accessLevel}
                        disabled={busy || tierOf(a.user.role) === "INTERN"}
                        onChange={(level) =>
                          void act(() =>
                            api.assign({
                              telegramAccountId: a.telegramAccountId,
                              userId: a.user.id,
                              accessLevel: level,
                            }),
                          )
                        }
                      />
                    )}

                    {canAssign && (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        disabled={busy}
                        onClick={() => void act(() => api.revokeAssignment(a.id))}
                        aria-label={`Отозвать доступ у ${a.user.displayName}`}
                        title="Отозвать доступ"
                        className="flex-none text-muted-foreground hover:text-destructive"
                      >
                        <TrashIcon className="size-4" />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {canAssign && (
            <div className="flex flex-col gap-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Добавить сотрудника
              </p>

              <div className="relative">
                <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Имя сотрудника"
                  aria-label="Поиск сотрудника"
                  className={cn(FIELD, "w-full pl-8")}
                />
              </div>

              {candidates.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {query.trim()
                    ? "Никого не нашлось."
                    : "Доступ выдан всем сотрудникам."}
                </p>
              ) : (
                <ul className="flex max-h-56 list-none flex-col gap-1.5 overflow-y-auto p-0">
                  {candidates.map((u) => (
                    <li
                      key={u.id}
                      className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-heading">
                          {u.displayName}
                        </span>
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {u.role}
                        </span>
                      </span>

                      {/*
                        Стажёру предлагается только чтение: «работа» для него
                        всё равно схлопнется потолком роли, и предложить её
                        значило бы обещать несуществующее.
                      */}
                      {tierOf(u.role) === "INTERN" ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          onClick={() =>
                            void act(() =>
                              api.assign({
                                telegramAccountId: account?.id ?? "",
                                userId: u.id,
                                accessLevel: "READ_ONLY",
                              }),
                            )
                          }
                          className="flex-none"
                        >
                          Дать чтение
                        </Button>
                      ) : (
                        <div className="flex flex-none items-center gap-1">
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busy}
                            onClick={() =>
                              void act(() =>
                                api.assign({
                                  telegramAccountId: account?.id ?? "",
                                  userId: u.id,
                                  accessLevel: "READ_ONLY",
                                }),
                              )
                            }
                          >
                            Чтение
                          </Button>
                          <Button
                            size="sm"
                            disabled={busy}
                            onClick={() =>
                              void act(() =>
                                api.assign({
                                  telegramAccountId: account?.id ?? "",
                                  userId: u.id,
                                  accessLevel: "MANAGE",
                                }),
                              )
                            }
                          >
                            Работа
                          </Button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {canAssignSenior && account && (
            <div className="flex flex-col gap-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Владелец аккаунта
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Старший распоряжается доступом к закреплённым за ним аккаунтам.
                {owner && ` Сейчас: ${owner.displayName}.`}
              </p>

              {seniors.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Старших менеджеров нет.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {seniors.map((s) => {
                    const current = owner?.id === s.id;
                    return (
                      <Button
                        key={s.id}
                        size="sm"
                        variant={current ? "secondary" : "ghost"}
                        disabled={busy || current}
                        onClick={() =>
                          void act(() => api.assignSenior(account.id, s.id))
                        }
                        className="gap-1.5"
                      >
                        {current && <CheckIcon className="size-3.5" />}
                        {s.displayName}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Закрыть
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Переключатель уровня прямо в строке: чтение ↔ работа. */
function LevelSwitch({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled: boolean;
  onChange: (level: string) => void;
}) {
  return (
    <div className="flex flex-none items-center rounded-full border border-border bg-secondary/60 p-0.5">
      {["READ_ONLY", "MANAGE"].map((level) => (
        <button
          key={level}
          type="button"
          disabled={disabled}
          aria-pressed={value === level}
          onClick={() => value !== level && onChange(level)}
          className={cn(
            "rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors disabled:opacity-45",
            value === level
              ? "bg-card text-brand shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {LEVEL_LABELS[level]}
        </button>
      ))}
    </div>
  );
}
